import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight, LuBookOpen, LuListChecks } from 'react-icons/lu';
import { courseState, percentOf, STATE_LABEL, STATE_TONE } from './courseProgress';
import { Button } from '../../ui/Button';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Badge, Meter } from '../../ui/Labels';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { courseApi } from '../../lib/courseApi';

const STAGGER_LIMIT = 12;

function CourseCard({ course, index }) {
  const t = useStrings();
  const state = courseState(course.solvedCount, course.questionCount);
  const progress = t('courses.progress', { solved: course.solvedCount, total: course.questionCount });
  return (
    <li style={{ '--i': Math.min(index, STAGGER_LIMIT) }}>
      <Link to={`/courses/${course.id}`} className="panel lit course-card" data-state={state} viewTransition>
        <div className="course-card__top">
          <span className="course-card__icon" aria-hidden="true">
            <LuBookOpen />
          </span>
          <Badge tone={STATE_TONE[state]} dot={state === 'active'}>
            {t(STATE_LABEL[state])}
          </Badge>
        </div>
        <h2 className="course-card__title">{course.title}</h2>
        {course.description && <p className="course-card__description">{course.description}</p>}
        <div className="course-card__footer">
          <div className="course-card__numbers">
            <span className="course-card__count">
              <LuListChecks aria-hidden="true" />
              {t('courses.problemCount', { count: course.questionCount })}
            </span>
            <span className="course-card__percent tabular">{percentOf(course.solvedCount, course.questionCount)}%</span>
          </div>
          <Meter value={course.solvedCount} max={course.questionCount} label={progress} />
          <span className="course-card__progress">
            {progress}
            <LuArrowRight aria-hidden="true" className="course-card__go" />
          </span>
        </div>
      </Link>
    </li>
  );
}

export function CoursesPage() {
  const t = useStrings();
  const [state, setState] = useState({ status: 'loading', courses: [], error: null });

  useDocumentTitle(t('courses.title'));

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: 'loading' }));
    const response = await courseApi.list();
    setState(
      response.success
        ? { status: 'ready', courses: response.data, error: null }
        : { status: 'error', courses: [], error: response.error }
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    courseApi.list().then((response) => {
      if (cancelled) return;
      setState(
        response.success
          ? { status: 'ready', courses: response.data, error: null }
          : { status: 'error', courses: [], error: response.error }
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('courses.title')}</h1>
          <p className="page-subtitle">{t('courses.intro')}</p>
        </div>
      </header>

      {state.status === 'error' && (
        <Alert tone="danger" action={<Button size="sm" onClick={load}>{t('common.retry')}</Button>}>
          {t('courses.loadError')} {state.error}
        </Alert>
      )}

      {state.status === 'loading' && (
        <ul className="course-grid" aria-hidden="true">
          {[0, 1, 2].map((key) => (
            <li key={key} className="panel course-card course-card--skeleton">
              <Skeleton width={36} height={36} />
              <Skeleton width="70%" height={20} />
              <Skeleton width="90%" />
              <Skeleton height={6} />
            </li>
          ))}
        </ul>
      )}

      {state.status === 'ready' && state.courses.length === 0 && (
        <div className="panel">
          <EmptyState icon={LuBookOpen} title={t('courses.emptyTitle')} text={t('courses.emptyText')} />
        </div>
      )}

      {state.status === 'ready' && state.courses.length > 0 && (
        <ul className="course-grid stagger">
          {state.courses.map((course, index) => (
            <CourseCard key={course.id} course={course} index={index} />
          ))}
        </ul>
      )}
    </div>
  );
}
